import { Dialog, TextField } from "@mui/material";
import { DoraButton, UFDoraSelect, DoraSelect, UFDoraDatePicker, DoraCheckbox, UFDoraTextInput } from "dora-shared/frontend";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { faArrowLeft, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { nanoid } from "nanoid";
import { DateTime } from "luxon";
import { selectClients } from "../../../../ducks/data/clients/selectors";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import { selectFinanceConfiguration } from "../../../../ducks/data/finance/selectors";
import { selectCustomerContacts } from "../../../../ducks/data/economics/customer-contacts/selectors";
import { selectDepartments } from "../../../../ducks/data/departments/selectors";
import { selectDelivery } from "../../../../ducks/data/deliveries/selectors";
import * as actions from "../../../../ducks/data/economics/customer-contacts";
import { loadDepartments } from "../../../../ducks/data/departments";
import { sendInvoice } from "../../../../ducks/data/deliveries";
import { selectOrgSettings } from "../../../../ducks/data/org-settings/selectors";
import { selectFormDataForWhenOpeningModal } from "../../../../ducks/app/cargo-dialog/selectors";
import type {
  InvoiceFolderOptions,
  InvoiceFolderPriorityField,
  DeliveryAddressPart,
} from "dora-contracts";
import type { StopPlace } from "../../../../ducks/app/cargo-and-template-shared";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { selectCargoDocuments } from "../../../../ducks/data/cargo-documents/selectors";
import {
  earliestInvoiceDueDate,
  formatBasicStringDate,
  isValidInvoiceDueDate,
} from "../../../../helpers/date-helper";
import { useTranslation } from "react-i18next";
import ClientSelect from "../ClientSelect";
import "./InvoiceDialog.scss";
import { selectTruckDepartmentIdForCargo } from "../../../../ducks/data/trailers/selectors";
import { loadCargo } from "../../../../ducks/app/cargo-dialog";
import { Document } from "../../../../ducks/data/cargo-documents/types";
import clsx from "clsx";
import { DeferredInvoicesFolder } from "../../../../ducks/data/consolidated-invoices/types";
import axios from "../../../../axios";
import { notifyL } from "../../../../ducks/notifications";
import { useFeature } from "../../../../hooks";
import sortFoldersByName from "./sort-folders-by-name";
import formatDepartment from "../../../../helpers/formatDepartment";
import { createEmptyFolder } from "../../../../ducks/data/consolidated-invoices";
import { loadCustomerDeliveryLocations } from "../../../../ducks/data/economics/customer-delivery-locations";
import { selectDeliveryLocationsForClient } from "../../../../ducks/data/economics/customer-delivery-locations/selectors";

function getAddressPartValue(
  addressPart: DeliveryAddressPart,
  place: StopPlace | null
): string {
  if (!place) return "";
  switch (addressPart) {
    case "PLACE_NAME":
      return place.placeName;
    case "STREET_ADDRESS":
      return place.address;
    case "POSTCODE":
      return place.postcode;
    case "CITY":
      return place.city;
  }
}

function resolvePriorityFieldValue(
  spec: InvoiceFolderPriorityField,
  invoiceRef: string | null,
  firstPickupPlace: StopPlace | null,
  firstDropoffPlace: StopPlace | null
): string {
  if (spec.field === "INVOICE_REFERENCE") return invoiceRef || "";
  if (spec.field === "PICKUP_ADDRESS")
    return getAddressPartValue(spec.addressPart, firstPickupPlace);
  return getAddressPartValue(spec.addressPart, firstDropoffPlace);
}

function computeAutoFolder(
  folders: DeferredInvoicesFolder[],
  opts: InvoiceFolderOptions,
  invoiceRef: string | null,
  firstPickupPlace: StopPlace | null,
  firstDropoffPlace: StopPlace | null,
  autoselectEnabled: boolean
): { option: string; name?: string } | null {
  if (!autoselectEnabled || opts.mode === "MANUAL") return null;

  const tryValue = (
    value: string
  ): { option: string; name?: string } | null => {
    if (!value) return autoselectEnabled ? { option: "__add_new__" } : null;
    const match = folders.find((f) => f.name === value);
    if (match) return { option: value };
    return { option: "__add_new__", name: value };
  };

  if (opts.mode === "FROM_FIELD") {
    if (opts.field === "INVOICE_REFERENCE") return tryValue(invoiceRef || "");
    if (opts.field === "PICKUP_ADDRESS")
      return tryValue(getAddressPartValue(opts.addressPart, firstPickupPlace));
    return tryValue(getAddressPartValue(opts.addressPart, firstDropoffPlace));
  }

  // PRIORITY_ORDER: try each priority for an exact folder match first
  const v1 = resolvePriorityFieldValue(
    opts.priority1,
    invoiceRef,
    firstPickupPlace,
    firstDropoffPlace
  );
  if (v1 && folders.find((f) => f.name === v1)) return { option: v1 };

  const v2 = resolvePriorityFieldValue(
    opts.priority2,
    invoiceRef,
    firstPickupPlace,
    firstDropoffPlace
  );
  if (v2 && folders.find((f) => f.name === v2)) return { option: v2 };

  // No exact match – use first non-empty value to pre-fill, or just open Add new if flag is on
  const fallbackName = v1 || v2;
  if (!fallbackName)
    return autoselectEnabled ? { option: "__add_new__" } : null;
  return { option: "__add_new__", name: fallbackName };
}

type InvoiceDialogProps = {
  type: "DIRECT" | "CONSOLIDATED";
  linesIds: string[];
  deliveryId: string;
  cargoId: string;
  onClose: (linesInvoiced?: boolean) => void;
  clientId?: string;
  clientContact: string | null;
  invoiceRef: string | null;
};

type PaymentTermsResult = {
  paymentTermType: string;
  clientOrOrgTerms: "client" | "org";
};

type InvoiceDialogFormValues = {
  client: string | null;
  contactPerson: string | null;
  department: string | null;
  invoiceRef: string | null;
  attachmentIds: string[];
  folderOption?: string;
  folderName?: string;
  dueDate?: DateTime | null;
};

const InvoiceDialog = (props: InvoiceDialogProps) => {
  const { type, deliveryId, linesIds, onClose, cargoId } = props;
  const [step, setStep] = useState(1);
  const [reqInProgress, setReqInProgress] = useState(false);
  // While the invoice is being sent, the dialog stays put: closing it would
  // let the cargo be saved alongside the send, and it would lose the outcome.
  const closeUnlessSending = () => {
    if (!reqInProgress) {
      onClose();
    }
  };
  const { t } = useTranslation(["shipmentModal", "components", "folders"]);
  const financeIntegration = useSelector(selectFinanceConfiguration);
  const isEconomic = financeIntegration === "E-CONOMIC";
  const documents = useSelector(selectCargoDocuments(cargoId));
  const trailerDepartmentId = useSelector(
    selectTruckDepartmentIdForCargo(cargoId)
  );
  const foldersAutoselectEnabled = useFeature("invoice-folders-autoselect");
  const shouldShowDeliveryAddress = isEconomic;
  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState<
    string | null
  >(null);
  const orgSettings = useSelector(selectOrgSettings);
  const cargoFormData = useSelector(selectFormDataForWhenOpeningModal);
  // The surrounding shipment dialog's form (this dialog renders inside its
  // FormProvider). Used to read live, not-yet-persisted stop data.
  const shipmentForm = useFormContext();

  // Refs so the folder-loading async callback always sees the latest values
  const orgSettingsRef = useRef(orgSettings);
  const cargoFormDataRef = useRef(cargoFormData);
  const invoiceRefPropRef = useRef(props.invoiceRef);
  const foldersAutoselectEnabledRef = useRef(foldersAutoselectEnabled);
  useEffect(() => {
    orgSettingsRef.current = orgSettings;
  }, [orgSettings]);
  useEffect(() => {
    cargoFormDataRef.current = cargoFormData;
  }, [cargoFormData]);
  useEffect(() => {
    invoiceRefPropRef.current = props.invoiceRef;
  }, [props.invoiceRef]);
  useEffect(() => {
    foldersAutoselectEnabledRef.current = foldersAutoselectEnabled;
  }, [foldersAutoselectEnabled]);

  // Prevents the folderOption→folderName reset effect from wiping auto-selected names
  const skipFolderNameResetRef = useRef(false);

  const [pendingAutoFolder, setPendingAutoFolder] = useState<{
    option: string;
    name?: string;
  } | null>(null);

  const [consolidatedInvoicesFolders, setConsolidatedInvoicesFolders] =
    useState<DeferredInvoicesFolder[]>([]);
  const [paymentTerms, setPaymentTerms] = useState<PaymentTermsResult | null>(
    null
  );
  const [isLoadingPaymentTerms, setIsLoadingPaymentTerms] = useState(false);

  const shouldUseDueDate = useMemo(
    () =>
      isEconomic &&
      paymentTerms?.paymentTermType === "dueDate" &&
      type === "DIRECT",
    [isEconomic, paymentTerms, type]
  );

  const schema = useMemo(
    () =>
      yup.object().shape({
        client: yup.string().required(),
        contactPerson: yup.string().nullable(),
        department: yup.string().nullable(),
        folderOption: yup.string().optional(),
        folderName: yup
          .string()
          .optional()
          .notOneOf(
            ["", ...consolidatedInvoicesFolders.map((cif) => cif.name)],
            "aaa"
          ),
        dueDate: shouldUseDueDate
          ? yup
              .mixed<DateTime>()
              .required()
              .test(
                "not-before-invoice-date",
                (value) => !!value && isValidInvoiceDueDate(value)
              )
          : yup.mixed().nullable().optional(),
      }),
    [consolidatedInvoicesFolders, shouldUseDueDate]
  );

  const dispatch = useAppDispatch();
  const [documentsIdsSelection, setDocumentsIdsSelection] = useState<string[]>(
    []
  );

  useEffect(() => {
    dispatch(loadDepartments());
    dispatch(loadCargo(cargoId));
  }, [dispatch, cargoId]);

  const formMethods = useForm<InvoiceDialogFormValues>({
    defaultValues: {
      client: props.clientId || null,
      contactPerson: props.clientContact || null,
      department: trailerDepartmentId,
      attachmentIds: [],
    },
    resolver: yupResolver(schema, {
      abortEarly: false,
    }),
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const {
    formState: { errors, isValid },
    control,
    watch: watchFormChanges,
    setValue,
    getValues,
    register,
    handleSubmit,
  } = formMethods;

  const clientWatch = watchFormChanges("client");
  const folderOptionWatch = watchFormChanges("folderOption");

  useEffect(() => {
    if (skipFolderNameResetRef.current) {
      skipFolderNameResetRef.current = false;
      return;
    }
    setValue("folderName", undefined);
    setSelectedDeliveryAddressId(null);
  }, [folderOptionWatch, setValue]);

  useEffect(() => {
    if (!pendingAutoFolder) return;
    if (pendingAutoFolder.option === "__add_new__" && pendingAutoFolder.name) {
      skipFolderNameResetRef.current = true;
    }
    setValue("folderOption", pendingAutoFolder.option);
    if (pendingAutoFolder.name) setValue("folderName", pendingAutoFolder.name);
    setPendingAutoFolder(null);
  }, [pendingAutoFolder, setValue]);

  useEffect(() => {
    if (!isEconomic || !clientWatch || type !== "DIRECT") {
      setPaymentTerms(null);
      setIsLoadingPaymentTerms(false);
      return;
    }
    setPaymentTerms(null);
    setValue("dueDate", null);
    setIsLoadingPaymentTerms(true);
    axios
      .get(`/api/e-conomic/customers/${clientWatch}/payment-terms`)
      .then((res) => {
        setPaymentTerms(res.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsLoadingPaymentTerms(false);
      });
  }, [clientWatch, isEconomic, type, setValue]);

  useEffect(() => {
    if (type === "DIRECT") {
      return;
    }
    setConsolidatedInvoicesFolders([]);
    if (clientWatch) {
      setValue("folderOption", "default");
      setValue("folderName", undefined);
      axios
        .get(`/api/consolidated-invoicing/folders/${clientWatch}`)
        .then(async (res) => {
          const data: DeferredInvoicesFolder[] = res.data;
          setConsolidatedInvoicesFolders(data);

          const firstPickupPlace =
            cargoFormDataRef.current?.pickupList?.[0]?.place ?? null;
          const firstDropoffPlace =
            cargoFormDataRef.current?.dropoffList?.[0]?.place ?? null;
          const autoResult = computeAutoFolder(
            data,
            orgSettingsRef.current.invoiceFolderOptions,
            invoiceRefPropRef.current,
            firstPickupPlace,
            firstDropoffPlace,
            foldersAutoselectEnabledRef.current
          );
          if (autoResult) {
            setPendingAutoFolder(autoResult);
          }
        })
        .catch((err) => {
          dispatch(
            notifyL({
              type: "error",
              namespace: "notifications",
              key: "errorLoadingFolders",
            })
          );
          console.error(err);
        });
    }
  }, [clientWatch, dispatch, type, setValue]);

  useEffect(() => {
    setValue("department", trailerDepartmentId);
  }, [trailerDepartmentId, setValue]);

  const delivery = useSelector(selectDelivery(deliveryId));
  const clients = useSelector(selectClients);
  const [
    shouldShowInvoiceDepartmentDropdown,
    setShouldShowInvoiceDepartmentDropdown,
  ] = useState(false);
  const departments = useSelector(selectDepartments);
  const contacts = useSelector(
    selectCustomerContacts(watchFormChanges("client"))
  );

  const resolveContactPerson = useCallback(
    (clientId?: string | null) => {
      setValue(
        "contactPerson",
        clientId === delivery.clientId ? delivery.customerContactId : null
      );
    },
    [setValue, delivery]
  );

  const reloadCustomerContacts = useCallback(
    (clientId?: string | null) => {
      if (clientId) {
        dispatch(actions.loadCustomerContacts(clientId));
        setValue("contactPerson", null);
      }
    },
    [dispatch, setValue]
  );

  useEffect(() => {
    const subscription = watchFormChanges((value, changes) => {
      const formClientId = value.client;
      if (changes.name?.includes("client")) {
        resolveContactPerson(formClientId);
        reloadCustomerContacts(formClientId);
      }
    });
    return () => subscription.unsubscribe();
  }, [watchFormChanges, resolveContactPerson, reloadCustomerContacts]);

  useEffect(() => {
    if (departments.length > 0) {
      setShouldShowInvoiceDepartmentDropdown(true);
    }
  }, [departments]);

  const onSendInvoiceClick = async () => {
    const {
      client,
      contactPerson,
      department,
      folderOption,
      folderName,
      dueDate,
    } = getValues();
    setReqInProgress(true);
    // When creating a new folder with a default delivery address, create the
    // folder first so it carries the address (and is validated against the
    // client). The invoice send below upserts the folder by name, which is a
    // no-op for the now-existing folder, preserving the address.
    if (
      type === "CONSOLIDATED" &&
      folderOption === "__add_new__" &&
      folderName &&
      shouldShowDeliveryAddress &&
      selectedDeliveryAddressId &&
      client
    ) {
      try {
        await dispatch(
          createEmptyFolder({
            clientId: client,
            folderName,
            deliveryAddressId: selectedDeliveryAddressId,
          })
        ).unwrap();
      } catch {
        // The createEmptyFolder thunk has already reported it and shown the error toast;
        // don't send the invoice, keep the dialog open so the user can retry.
        setReqInProgress(false);
        return;
      }
    }
    try {
      await dispatch(
        sendInvoice({
          clientId: client!,
          type,
          deliveryId,
          ...(type === "CONSOLIDATED" && {
            folderName:
              folderName ||
              (folderOption === "default" ? undefined : folderOption),
          }),
          ...(type === "DIRECT" &&
            shouldShowDeliveryAddress && {
              deliveryAddressId: selectedDeliveryAddressId,
            }),
          attachmentFileIds: documentsIdsSelection,
          customerContactId: contactPerson || null,
          selectedLineIds: linesIds,
          departmentId: department || null,
          dueDate: shouldUseDueDate && dueDate ? dueDate.toISODate() : null,
        })
      ).unwrap();
    } catch {
      // The sendInvoice thunk has already reported the failure and shown the
      // error toast (on a conflict it also loaded the fresh delivery). Nothing
      // was invoiced, so keep the dialog open for another try instead of
      // telling the cargo dialog the lines were invoiced.
      setReqInProgress(false);
      return;
    }
    setReqInProgress(false);
    onClose(true);
  };

  const clientInputId = useMemo(() => nanoid(), []);
  const contactInputId = useMemo(() => nanoid(), []);
  const folderInputId = useMemo(() => nanoid(), []);
  const departmentInputId = useMemo(() => nanoid(), []);
  const invoiceRefInputId = useMemo(() => nanoid(), []);
  const dueDateInputId = useMemo(() => nanoid(), []);
  const deliveryAddressInputId = useMemo(() => nanoid(), []);

  const deliveryLocations = useSelector(
    selectDeliveryLocationsForClient(clientWatch ?? "")
  );
  const deliveryAddressOptions = useMemo(
    () =>
      deliveryLocations.map((location) => ({
        value: location.id,
        label:
          [
            location.address,
            location.postalCode,
            location.city,
            location.country,
          ]
            .filter(Boolean)
            .join(", ") || location.number,
      })),
    [deliveryLocations]
  );

  useEffect(() => {
    if (clientWatch && shouldShowDeliveryAddress) {
      dispatch(loadCustomerDeliveryLocations(clientWatch));
    }
  }, [clientWatch, shouldShowDeliveryAddress, dispatch]);

  // The delivery address the first dropoff was picked from (if any). This lives
  // only in the live shipment form state — it is not persisted server-side, so
  // it cannot be read from the (server-derived) redux cargo form data. The
  // renders inside the shipment dialog's FormProvider, so we read
  // it from the parent form context.
  const firstDropoffDeliveryAddressId =
    (shipmentForm.watch("dropoffList[0].place.deliveryAddressId") as
      | string
      | null
      | undefined) ?? null;

  // When the delivery-address dropdown is shown — always for a direct invoice,
  // or for a new consolidated folder — default it to the first dropoff delivery
  // address, provided it's a valid option for the selected client. Depends on
  // folderOptionWatch so it re-applies after the folder-option reset effect
  // above clears the selection.
  useEffect(() => {
    if (
      shouldShowDeliveryAddress &&
      (type === "DIRECT" || folderOptionWatch === "__add_new__") &&
      firstDropoffDeliveryAddressId &&
      deliveryAddressOptions.some(
        (option) => option.value === firstDropoffDeliveryAddressId
      )
    ) {
      setSelectedDeliveryAddressId(firstDropoffDeliveryAddressId);
    }
  }, [
    shouldShowDeliveryAddress,
    type,
    folderOptionWatch,
    firstDropoffDeliveryAddressId,
    deliveryAddressOptions,
  ]);

  const onDocumentSelection = (id: string) => () => {
    let selection = [...documentsIdsSelection];
    if (selection.includes(id)) {
      selection = selection.filter((i) => i !== id);
    } else {
      selection.push(id);
    }
    setDocumentsIdsSelection(selection);
  };

  const isAttachableDocument = (document: Document) => {
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    return allowedTypes.includes(document.fileType);
  };

  const attachableDocuments = useMemo(
    () => documents.filter(isAttachableDocument),
    [documents]
  );

  const onAllDocumentsSelection = () => {
    if (documentsIdsSelection.length === attachableDocuments.length) {
      setDocumentsIdsSelection([]);
    } else {
      setDocumentsIdsSelection(attachableDocuments.map((d) => d.id));
    }
  };

  const onBackButtonClick = () => {
    setStep(1);
    setDocumentsIdsSelection([]);
  };

  const folderOptions = useMemo(
    () => [
      ...sortFoldersByName([
        {
          label: t("folders:defaultTab"),
          value: "default",
        },
        ...consolidatedInvoicesFolders.map((folder) => ({
          label: folder.name,
          value: folder.name,
        })),
      ]),
      {
        label: t("folders:addNew"),
        value: "__add_new__",
      },
    ],
    [consolidatedInvoicesFolders, t]
  );

  const deliveryAddressDropdown = (
    <div className="invoice-dialog__input-row">
      <div>
        <label htmlFor={deliveryAddressInputId}>
          {t("folders:defaultDeliveryAddressLabel")}
        </label>
      </div>
      <DoraSelect
        fullWidth
        clearable
        className="w-full"
        id={deliveryAddressInputId}
        placeholder={t("folders:defaultDeliveryAddressPlaceholder")}
        value={selectedDeliveryAddressId ?? undefined}
        options={deliveryAddressOptions}
        onChange={(v) => setSelectedDeliveryAddressId(v || null)}
      />
    </div>
  );

  const onSubmit = handleSubmit(() => setStep(2));

  return (
    <Dialog open onClose={closeUnlessSending}>
      <div className="invoice-dialog">
        {step === 1 && (
          <FormProvider {...formMethods}>
            <form onSubmit={onSubmit}>
              <div className="invoice-dialog__header">
                <h2 className="invoice-dialog__header-title">
                  {type === "DIRECT"
                    ? t("shipmentModal:cards.finances.invoiceButton")
                    : t("shipmentModal:cards.finances.sendConsolidatedButton")}
                </h2>
                <DoraButton
                  variant="ghost"
                  type="button"
                  className="invoice-dialog__header-close"
                  onClick={() => onClose()}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </DoraButton>
              </div>
              <div className="invoice-dialog__body">
                <div className="invoice-dialog__input-row">
                  <div>
                    <label htmlFor={clientInputId}>
                      {t("shipmentModal:inputs.clientLabel")}
                      <span aria-hidden>*</span>
                    </label>
                  </div>
                  <ClientSelect
                    autofocus
                    autoselect
                    name="client"
                    refreshButtonLabel={t(
                      "shipmentModal:inputs.clientRefreshButtonLabel"
                    )}
                    control={control}
                    clients={clients}
                    id={clientInputId}
                    className="w-full"
                    placeholder={t("shipmentModal:cards.finances.chooseClient")}
                    error={!!errors.client}
                  />
                </div>
                {shouldUseDueDate && (
                  <div className="invoice-dialog__input-row">
                    <div>
                      <label htmlFor={dueDateInputId}>
                        {t("shipmentModal:cards.finances.dueDate")}
                        <span aria-hidden>*</span>
                      </label>
                    </div>
                    <div>
                      <UFDoraDatePicker
                        fullWidth
                        minDate={earliestInvoiceDueDate()}
                        id={dueDateInputId}
                        control={control}
                        name="dueDate"
                        error={!!errors.dueDate}
                      />
                    </div>
                  </div>
                )}
                {type === "CONSOLIDATED" && (
                  <>
                    <div className="invoice-dialog__input-row">
                      <div>
                        <label htmlFor={folderInputId}>
                          {t("folders:folderLabel")}
                        </label>
                      </div>
                      <UFDoraSelect
                        fullWidth
                        autoselect
                        placeholder={t("folders:folderPlaceholder")}
                        className="w-full"
                        id={folderInputId}
                        control={control}
                        name={"folderOption"}
                        options={folderOptions}
                      ></UFDoraSelect>
                    </div>
                    {folderOptionWatch === "__add_new__" && (
                      <div className="invoice-dialog__input-row">
                        <div>
                          <label htmlFor={folderInputId}>
                            {t("folders:folderNameLabel")}
                          </label>
                        </div>
                        <UFDoraTextInput
                          error={!!errors.folderName}
                          id={folderInputId}
                          placeholder={t("folders:folderNamePlaceholder")}
                          register={register("folderName")}
                        />
                      </div>
                    )}
                    {folderOptionWatch === "__add_new__" &&
                      shouldShowDeliveryAddress &&
                      deliveryAddressDropdown}
                  </>
                )}
                {type === "DIRECT" &&
                  shouldShowDeliveryAddress &&
                  deliveryAddressDropdown}
                <div className="invoice-dialog__input-row">
                  <div>
                    <label htmlFor={contactInputId}>
                      {t("shipmentModal:inputs.contactPlaceholder2")}
                    </label>
                  </div>
                  <UFDoraSelect
                    fullWidth
                    autoselect
                    placeholder={t("shipmentModal:inputs.contactPlaceholder2")}
                    className="w-full"
                    id={contactInputId}
                    control={control}
                    name={"contactPerson"}
                    options={
                      contacts?.map((contact) => ({
                        label: contact.name,
                        value: contact.id,
                      })) || []
                    }
                  ></UFDoraSelect>
                </div>
                {shouldShowInvoiceDepartmentDropdown && (
                  <div className="invoice-dialog__input-row">
                    <div>
                      <label htmlFor={departmentInputId}>
                        {t("shipmentModal:cards.finances.department")}
                      </label>
                    </div>
                    <UFDoraSelect
                      fullWidth
                      autoselect
                      className="w-full"
                      placeholder={t("shipmentModal:cards.finances.department")}
                      id={departmentInputId}
                      options={departments.map((department) => ({
                        label: formatDepartment(department),
                        value: department.id,
                      }))}
                      name="department"
                      control={control}
                    />
                  </div>
                )}
                <div className="invoice-dialog__input-row">
                  <div>
                    <label htmlFor={invoiceRefInputId}>
                      {t("shipmentModal:inputs.invoiceRefLabel")}
                    </label>
                  </div>
                  <TextField
                    disabled
                    value={props.invoiceRef}
                    id={invoiceRefInputId}
                    placeholder={t("shipmentModal:inputs.invoiceRefLabel")}
                  />
                </div>
              </div>
              <div className="invoice-dialog__footer">
                <DoraButton
                  variant="primary"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                  }}
                >
                  {t("components:buttonLabels.cancel")}
                </DoraButton>
                <DoraButton
                  variant="primary-filled"
                  type="submit"
                  disabled={
                    !isValid ||
                    (type === "DIRECT" && isEconomic && isLoadingPaymentTerms)
                  }
                >
                  {t("components:buttonLabels.next")}
                </DoraButton>
              </div>
            </form>
          </FormProvider>
        )}
        {step === 2 && (
          <>
            <div className="invoice-dialog__header">
              <DoraButton
                className="invoice-dialog__back-button"
                variant="primary"
                onClick={onBackButtonClick}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </DoraButton>
              <h2 className="invoice-dialog__header-title">
                {t("shipmentModal:cards.finances.attachDocuments")}
              </h2>
              <DoraButton
                variant="ghost"
                className="invoice-dialog__header-close"
                onClick={closeUnlessSending}
                disabled={reqInProgress}
              >
                <FontAwesomeIcon icon={faTimes} />
              </DoraButton>
            </div>
            {documents.length ? (
              <table className="invoice-dialog__table">
                <thead>
                  <tr style={{ paddingTop: 12 }}>
                    <th>
                      <DoraCheckbox
                        className="flex justify-center"
                        checked={
                          documentsIdsSelection.length ===
                          attachableDocuments.length
                        }
                        onChange={onAllDocumentsSelection}
                      />
                    </th>
                    <th style={{ padding: 12 }}>
                      {t("shipmentModal:cards.finances.fileName")}
                    </th>
                    <th style={{ padding: 12 }}>
                      {t("shipmentModal:cards.finances.dateUploaded")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((document, i) => {
                    const isAttachable = isAttachableDocument(document);
                    const isSelected = documentsIdsSelection.includes(
                      document.id
                    );
                    const classes = clsx({
                      "invoice-document__unselectable": !isAttachable,
                      "invoice-document__selected": isSelected,
                    });
                    return (
                      <tr key={i} className={classes}>
                        <td>
                          <DoraCheckbox
                            className="flex justify-center"
                            checked={documentsIdsSelection.includes(
                              document.id
                            )}
                            disabled={!isAttachable}
                            onChange={onDocumentSelection(document.id)}
                          />
                        </td>
                        <td>
                          <div className="invoice-dialog__document-row">
                            {/*<div className="invoice-document-row__img-wrapper">*/}
                            {/*  <img src={document.imgUrl} alt="" />*/}
                            {/*</div>*/}
                            <div style={{ padding: 12 }}>{document.name}</div>
                          </div>
                        </td>
                        <td>
                          <div style={{ padding: 12 }}>
                            {formatBasicStringDate(document.addedAt)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="pt-4 px-5 pb-3 text-center">
                {t("shipmentModal:cards.finances.noDocuments")}
              </div>
            )}
            <div className="invoice-dialog__footer">
              <DoraButton
                variant="primary"
                onClick={closeUnlessSending}
                disabled={reqInProgress}
              >
                {t("components:buttonLabels.cancel")}
              </DoraButton>
              <DoraButton
                variant="primary-filled"
                onClick={onSendInvoiceClick}
                disabled={reqInProgress}
              >
                {t("shipmentModal:cards.finances.sendInvoiceButton")}
              </DoraButton>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default InvoiceDialog;
